import React, { createContext, useContext, useState, useEffect } from 'react';
import { getInitialFormData, hasFormDataChanged } from '../utils/leadFormUtils';

// Context for managing the lead form state
const NewKBLeadFormContext = createContext(null);

/**
 * Provider component for the lead form context
 * @param {Object} props - Component props
 * @param {Object} props.initialData - Initial form data (optional)
 * @param {Object} props.metadata - Reference data for the form
 * @param {React.ReactNode} props.children - Child components
 */
export const NewKBLeadFormProvider = ({ 
  initialData = null,
  metadata = {}, 
  children 
}) => {
  // Core form state
  const [formData, setFormData] = useState(initialData || getInitialFormData());
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Track original data for comparison
  const [originalData, setOriginalData] = useState(null);
  
  // Update form state when initial data changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
    } else {
      setFormData(getInitialFormData());
      setOriginalData(null);
    }
    setIsFormDirty(false);
    setValidationErrors([]);
  }, [initialData]);
  
  // Check if form is dirty when data changes
  useEffect(() => {
    setIsFormDirty(hasFormDataChanged(originalData, formData));
  }, [formData, originalData]);
  
  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      let newValue = value;
      let updatedData = { ...prev };
      
      // Handle numeric fields conversion
      if (['roomCount', 'residenceGuestCount', 'guestCapacity'].includes(field)) {
        newValue = value === '' ? null : 
                  isNaN(Number(value)) ? prev[field] : Number(value);
      }
      
      // Handle nested fields (e.g., 'budget.amount')
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedData[parent] = { ...prev[parent], [child]: newValue };
      } else {
        updatedData[field] = newValue;
      }
      
      return updatedData;
    });
  };
  
  // Handle event-specific changes
  const handleEventChange = (index, field, value) => {
    console.log('[NewKBLeadFormContext] handleEventChange called:', { index, field, value });
    
    setFormData(prev => {
      const events = [...prev.events];
      let newValue = value;
      
      // Handle numeric fields conversion
      if (field === 'guestCount') {
        newValue = value === '' ? null : 
                  isNaN(Number(value)) ? events[index][field] : Number(value);
      }
      
      // Special handling for event type
      if (field === 'type') {
        // If this is a valid event type ID, store it directly
        // We'll keep it as a string ID instead of trying to convert to object
        console.log('[NewKBLeadFormContext] Updating event type:', { 
          oldValue: events[index]?.type,
          newValue: value 
        });
      }
      
      events[index] = {
        ...events[index],
        [field]: newValue
      };
      
      console.log('[NewKBLeadFormContext] Updated events:', {
        updatedEvent: events[index],
        allEvents: events
      });
      
      return { ...prev, events };
    });
  };
  
  // Add a new event
  const addEvent = () => {
    setFormData(prev => ({
      ...prev,
      events: [
        ...prev.events,
        {
          type: '',
          guestCount: null,
          startTime: '',
          endTime: '',
          notes: ''
        }
      ]
    }));
  };
  
  // Remove an event
  const removeEvent = (index) => {
    console.log('[NewKBLeadFormContext] Removing event at index:', index);
    
    setFormData(prev => {
      const updatedEvents = prev.events.filter((_, i) => i !== index);
      console.log('[NewKBLeadFormContext] Events after removing:', updatedEvents);
      
      return {
        ...prev,
        events: updatedEvents
      };
    });
  };
  
  // Add a new tag
  const addTag = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };
  
  // Remove a tag
  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  // Add a new city
  const addCity = (city) => {
    if (!formData.locations.cities.includes(city)) {
      setFormData(prev => ({
        ...prev,
        locations: {
          ...prev.locations,
          cities: [...prev.locations.cities, city]
        }
      }));
    }
  };
  
  // Remove a city
  const removeCity = (city) => {
    setFormData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        cities: prev.locations.cities.filter(c => c !== city)
      }
    }));
  };
  
  // Add a new venue
  const addVenue = (venue) => {
    if (!formData.locations.venues.includes(venue)) {
      setFormData(prev => ({
        ...prev,
        locations: {
          ...prev.locations,
          venues: [...prev.locations.venues, venue]
        }
      }));
    }
  };
  
  // Remove a venue
  const removeVenue = (venue) => {
    setFormData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        venues: prev.locations.venues.filter(v => v !== venue)
      }
    }));
  };
  
  // Add a new entertainment option
  const addEntertainmentOption = (option) => {
    if (!formData.entertainmentOptions.includes(option)) {
      setFormData(prev => ({
        ...prev,
        entertainmentOptions: [...prev.entertainmentOptions, option]
      }));
    }
  };
  
  // Remove an entertainment option
  const removeEntertainmentOption = (option) => {
    setFormData(prev => ({
      ...prev,
      entertainmentOptions: prev.entertainmentOptions.filter(o => o !== option)
    }));
  };
  
  // Add a new event date
  const addEventDate = (date) => {
    setFormData(prev => ({
      ...prev,
      eventDates: [...prev.eventDates, date]
    }));
  };
  
  // Update an event date
  const updateEventDate = (index, date) => {
    setFormData(prev => {
      const eventDates = [...prev.eventDates];
      eventDates[index] = date;
      return { ...prev, eventDates };
    });
  };
  
  // Remove an event date
  const removeEventDate = (index) => {
    setFormData(prev => ({
      ...prev,
      eventDates: prev.eventDates.filter((_, i) => i !== index)
    }));
  };
  
  // Reset form to initial state
  const resetForm = () => {
    setFormData(initialData || getInitialFormData());
    setIsFormDirty(false);
    setValidationErrors([]);
  };
  
  // Extract relevant metadata for the form
  const { 
    stages,
    leadSources,
    users,
    eventTypes,
    budgetRanges,
    locations,
    paymentTypes,
    studioTypes
  } = metadata;

  // Get context value
  const contextValue = {
    formData,
    setFormData,
    isFormDirty,
    validationErrors,
    setValidationErrors,
    handleFormChange,
    handleEventChange,
    addEvent,
    removeEvent,
    addTag,
    removeTag,
    addCity,
    removeCity,
    addVenue,
    removeVenue,
    addEntertainmentOption,
    removeEntertainmentOption,
    addEventDate,
    updateEventDate,
    removeEventDate,
    resetForm,
    // Make metadata available directly for easier access
    availableStages: stages || [],
    availableLeadSources: leadSources || [],
    availableUsers: users || [],
    availableEventTypes: eventTypes || [],
    availableBudgetRanges: budgetRanges || [],
    availableLocations: locations || {},
    availablePaymentTypes: paymentTypes || [],
    availableStudioTypes: studioTypes || [],
    // Also include full metadata for backward compatibility
    metadata
  };
  
  return (
    <NewKBLeadFormContext.Provider value={contextValue}>
      {children}
    </NewKBLeadFormContext.Provider>
  );
};

/**
 * Custom hook to use the lead form context
 * @returns {Object} Lead form context
 */
export const useNewKBLeadForm = () => {
  const context = useContext(NewKBLeadFormContext);
  
  if (!context) {
    throw new Error('useNewKBLeadForm must be used within a NewKBLeadFormProvider');
  }
  
  return context;
};
